import { paths } from '@/config/paths';

import type { ParentConnectSessionRow } from '../api/get-parent-connect-sessions';
import { Schedule } from '../types';

// Defensive fallback so a bad/empty timezone never throws inside
// Intl.DateTimeFormat and blanks out the whole calendar.
const SAFE_DEFAULT_TZ = 'Asia/Kolkata';

// Minimum lead time (seconds) before a trainer-initiated active session
// can be cancelled / rescheduled by the parent. Mirrors the BSS-side
// `PARENT_ACTIVE_CANCEL_MIN_LEAD_SECONDS` gate so we only show the
// drawer affordance when the backend will actually allow the action.
const ACTIVE_CANCEL_MIN_LEAD_SECONDS = 2 * 60 * 60;

// Mirrors BSS `PARENT_RESCHEDULE_HARD_CAP`: a parent may counter-
// reschedule their own pending request at most twice. Used to render
// "Reschedule attempts left: N" on parent-initiated open requests.
const PARENT_RESCHEDULE_CAP = 2;

// True when a parent may still cancel / reschedule this row: it's a
// trainer-initiated session, currently active (accepted), and the
// start is at least 2h out. Everything else (parent-initiated rows,
// pending / completed / cancelled rows, or anything inside the 2h
// window) is intentionally excluded.
const canCancelReschedule = (row: ParentConnectSessionRow): boolean => {
  const isActive = (row.session_status ?? '').toLowerCase() === 'active';
  const isTrainerInitiated =
    (row.initiated_by ?? '').toLowerCase() === 'trainer';
  if (!isActive || !isTrainerInitiated || !row.session_id) {
    return false;
  }
  const nowSeconds = Date.now() / 1000;
  return row.start_time - nowSeconds >= ACTIVE_CANCEL_MIN_LEAD_SECONDS;
};

// Derive the Parent Connect pill state from the raw TPC row.
//   pending + last_rescheduled_role === 'trainer' -> request_pending
//       (trainer suggested a new time; the parent must accept / rebook,
//        so this pill deep-links to the respond page)
//   pending (parent raised it / parent rescheduled) -> request_sent
//   active    -> accepted
//   completed -> completed
//   cancelled -> cancelled
//   deleted   -> removed
const resolveParentConnectState = (
  row: ParentConnectSessionRow
): Schedule.ParentConnectState => {
  const status = (row.session_status ?? '').toLowerCase();
  switch (status) {
    case 'active': {
      return 'accepted';
    }
    case 'completed': {
      return 'completed';
    }
    case 'cancelled': {
      return 'cancelled';
    }
    case 'deleted': {
      return 'removed';
    }
    default: {
      // pending (and any unknown status) -> a still-open request
      return (row.last_rescheduled_role ?? '').toLowerCase() === 'trainer'
        ? 'request_pending'
        : 'request_sent';
    }
  }
};

const buildFormatter = (
  timezone: string,
  options: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: timezone, ...options });
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: SAFE_DEFAULT_TZ,
      ...options,
    });
  }
};

// Convert raw parent-connect rows into calendar Lessons. `date`/`day`/
// `time` are formatted from the epoch `start_time` in the calendar's
// display timezone so they line up with the "All times are in <tz>"
// header and the rest of the schedule rows.
//
// `status` is left undefined for PC rows: the pill is driven entirely by
// `pcState` (so it always shows and never participates in the "first
// upcoming" highlight that real lessons use).
export const mapParentConnectToLessons = (
  rows: ParentConnectSessionRow[],
  timezone?: string
): Schedule.Lesson[] => {
  const tz = timezone ?? SAFE_DEFAULT_TZ;
  // `date` must stay parseable by `new Date(...)` (the calendar groups,
  // sorts and strips the year off it) -> keep the "Month D, YYYY" shape
  // that /user/v2/sessions already uses.
  const dateFormatter = buildFormatter(tz, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const dayFormatter = buildFormatter(tz, { weekday: 'long' });
  const timeFormatter = buildFormatter(tz, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return rows
    .filter(row => !!row?.start_time)
    .map(row => {
      const when = new Date(row.start_time * 1000);
      const pcState = resolveParentConnectState(row);
      // Only the trainer-rescheduled state is actionable, and only when
      // we resolved a variant_id (the respond page is variant-keyed).
      // Without it we degrade to a non-clickable label.
      const pcRespondHref =
        pcState === 'request_pending' && row.variant_id
          ? paths.app.parentConnectRespond.getHref({
              queryParams: { variant_id: row.variant_id },
            })
          : undefined;

      // Trainer-initiated active rows (>= 2h lead) get a single deep link
      // to the cancel / reschedule landing page, keyed by session_id (the
      // drawer always carries the concrete row id). The landing page then
      // offers both Cancel and Cancel-and-Reschedule.
      const pcCancelRescheduleHref = canCancelReschedule(row)
        ? paths.app.parentConnectCancelReschedule.getHref({
            queryParams: { session_id: row.session_id },
          })
        : undefined;

      // Parent-initiated open requests (still pending / trainer-
      // rescheduled) surface how many counter-reschedules the parent has
      // left. Trainer-initiated rows and settled states (accepted /
      // completed / cancelled / removed) don't show it.
      const isParentInitiated =
        (row.initiated_by ?? '').toLowerCase() === 'parent';
      const isOpenRequest =
        pcState === 'request_sent' || pcState === 'request_pending';
      const pcRescheduleAttemptsLeft =
        isParentInitiated && isOpenRequest
          ? Math.max(
              0,
              PARENT_RESCHEDULE_CAP - (row.parent_reschedule_request_count ?? 0)
            )
          : undefined;

      // A parent-initiated request the trainer hasn't acted on yet
      // (request_sent) gets a direct "Reschedule" deep link to the
      // edit-request page, as long as the parent still has attempts left.
      // request_pending rows are intentionally excluded - those route
      // through the respond page (Book New Slot) instead.
      const pcEditRequestHref =
        isParentInitiated &&
        pcState === 'request_sent' &&
        row.session_id &&
        (pcRescheduleAttemptsLeft ?? 0) > 0
          ? paths.app.parentConnectEditRequest.getHref({
              queryParams: { session_id: row.session_id },
            })
          : undefined;

      return {
        name: 'Parent Connect',
        date: dateFormatter.format(when),
        day: dayFormatter.format(when),
        time: timeFormatter.format(when),
        start_time: row.start_time,
        variant: 'parent_connect' as const,
        pcState,
        pcRespondHref,
        pcCancelRescheduleHref,
        pcEditRequestHref,
        pcRescheduleAttemptsLeft,
      } satisfies Schedule.Lesson;
    });
};
